import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BellOff,
  Calendar,
  Camera,
  CreditCard,
  KeyRound,
  Loader2,
  Mail,
  Save,
  Trash2,
  User as UserIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import {
  useAgentSchedule,
  useChangePassword,
  useDeleteUserAvatar,
  useProfile,
  useUpdateAgentSchedule,
  useUpdateProfile,
  useUploadUserAvatar,
  type AgentScheduleDay,
} from '@/api/profile'
import { useAvailableCallerIds } from '@/api/agents'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CompanyTimezoneSection } from '@/components/dashboard/profile/CompanyTimezoneSection'
import { CompanyNameSection } from '@/components/dashboard/profile/CompanyNameSection'
import { PasskeysSection } from '@/components/dashboard/profile/PasskeysSection'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'
import { PhoneVerification, type EtapeCode } from '@/components/onboarding/PhoneVerification'

const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_AVATAR_SIZE = 5 * 1024 * 1024

export function ProfilePage() {
  const { t, i18n } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile, isLoading } = useProfile()
  const { data: availableCallerIds = [] } = useAvailableCallerIds()
  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadUserAvatar()
  const deleteAvatar = useDeleteUserAvatar()
  const queryClient = useQueryClient()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneStep, setPhoneStep] = useState<EtapeCode>('idle')
  const [jobTitle, setJobTitle] = useState('')
  const [language, setLanguage] = useState<'fr' | 'en'>('fr')
  const [preferredCallerId, setPreferredCallerId] = useState('')
  const [forwardNumber, setForwardNumber] = useState('')
  const [dndEnabled, setDndEnabled] = useState(false)
  const [smsPushEnabled, setSmsPushEnabled] = useState(true)
  const [smsEmailEnabled, setSmsEmailEnabled] = useState(false)

  const avatarInputRef = useRef<HTMLInputElement>(null)

  // Hydrater le state local depuis le profil chargé
  useEffect(() => {
    if (!profile) return
    setFirstName(profile.first_name || '')
    setLastName(profile.last_name || '')
    setJobTitle(profile.job_title || '')
    setLanguage((profile.language as 'fr' | 'en') || 'fr')
    setPreferredCallerId(profile.preferred_caller_id || '')
    setForwardNumber(profile.forward_number || '')
    setDndEnabled(Boolean(profile.dnd_enabled))
    setSmsPushEnabled(profile.sms_push_notifications_enabled !== false)
    setSmsEmailEnabled(profile.sms_email_notifications_enabled === true)
  }, [profile])

  // The mobile number receives the sign-in codes: it is saved only once its text
  // code is checked, never by "Save". Rehydrated only when the stored number or
  // its proof changes, so saving other fields does not wipe a code in progress.
  const storedPhone = profile?.phone_number || ''
  const phoneVerifiedAt = profile?.phone_verified_at || ''
  useEffect(() => {
    setPhoneNumber(storedPhone)
    setPhoneStep('idle')
  }, [storedPhone, phoneVerifiedAt])

  const onPhoneStep = (step: EtapeCode) => {
    setPhoneStep(step)
    if (step === 'ok') {
      toast.success(t('profile.phoneSaved', 'Cellulaire vérifié et enregistré'))
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
    }
  }

  // Détection des changements non sauvegardés (= footer sticky visible)
  const isDirty = useMemo(() => {
    if (!profile) return false
    return (
      firstName !== (profile.first_name || '') ||
      lastName !== (profile.last_name || '') ||
      jobTitle !== (profile.job_title || '') ||
      language !== ((profile.language as 'fr' | 'en') || 'fr') ||
      preferredCallerId !== (profile.preferred_caller_id || '') ||
      forwardNumber !== (profile.forward_number || '') ||
      dndEnabled !== Boolean(profile.dnd_enabled) ||
      smsPushEnabled !== (profile.sms_push_notifications_enabled !== false) ||
      smsEmailEnabled !== (profile.sms_email_notifications_enabled === true)
    )
  }, [
    profile,
    firstName,
    lastName,
    jobTitle,
    language,
    preferredCallerId,
    forwardNumber,
    dndEnabled,
    smsPushEnabled,
    smsEmailEnabled,
  ])

  const resetForm = () => {
    if (!profile) return
    setFirstName(profile.first_name || '')
    setLastName(profile.last_name || '')
    setJobTitle(profile.job_title || '')
    setLanguage((profile.language as 'fr' | 'en') || 'fr')
    setPreferredCallerId(profile.preferred_caller_id || '')
    setForwardNumber(profile.forward_number || '')
    setDndEnabled(Boolean(profile.dnd_enabled))
    setSmsPushEnabled(profile.sms_push_notifications_enabled !== false)
    setSmsEmailEnabled(profile.sms_email_notifications_enabled === true)
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error(t('profile.errors.avatarFormat', 'Format non supporté (JPEG, PNG, WebP)'))
      return
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error(t('profile.errors.avatarSize', 'Fichier trop volumineux (max 5 Mo)'))
      return
    }
    try {
      await uploadAvatar.mutateAsync(file)
      toast.success(t('profile.avatarUploaded', 'Photo de profil mise à jour'))
    } catch {
      toast.error(t('profile.errors.upload', "Erreur lors de l'upload"))
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    }
  }

  const handleAvatarDelete = async () => {
    try {
      await deleteAvatar.mutateAsync()
      toast.success(t('profile.avatarDeleted', 'Photo supprimée'))
    } catch {
      toast.error(t('profile.errors.delete', 'Erreur lors de la suppression'))
    }
  }

  // Linked account: the partner sets the email and mobile number, never this page.
  const linkedVia = profile?.linked_via || ''
  const typedPhone = phoneNumber.trim()
  const phoneProven = Boolean(phoneVerifiedAt) && typedPhone === storedPhone
  // Code block under the field: a new number, or a stored one never proven.
  const showPhoneCode =
    !linkedVia && (phoneStep !== 'idle' || (typedPhone !== '' && !phoneProven))

  const handleSave = async () => {
    try {
      const previousLanguage = profile?.language || 'fr'
      await updateProfile.mutateAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        job_title: jobTitle.trim() || undefined,
        language,
        forward_number: forwardNumber.trim() || undefined,
        // Only when changed here: Do not disturb may have been set since on the phone.
        dnd_enabled: dndEnabled !== Boolean(profile?.dnd_enabled) ? dndEnabled : undefined,
        sms_push_notifications_enabled: smsPushEnabled,
        sms_email_notifications_enabled: smsEmailEnabled,
        preferred_caller_id: preferredCallerId || undefined,
      })

      // Application instantanée du changement de langue : on switch i18next
      // et on remplace le segment locale dans l'URL pour rester cohérent.
      // Évite d'avoir à se déconnecter/reconnecter comme dans le Next legacy.
      if (language !== previousLanguage) {
        await i18n.changeLanguage(language)
        try {
          localStorage.setItem('preferred_locale', language)
        } catch {
          // ignore (private mode etc.)
        }
        const newPath = window.location.pathname.replace(
          /^\/(?:fr|en)(\/|$)/,
          `/${language}$1`,
        )
        navigate(newPath + window.location.search, { replace: true })
      }

      toast.success(t('profile.saved', 'Profil mis à jour'))
    } catch {
      toast.error(t('profile.errors.save', 'Erreur lors de la sauvegarde'))
    }
  }

  // Billing lives with the owner (the subscription is in their name).
  const isManager = profile?.access ? profile.access.billing : profile?.role === 'MANAGER'
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { locale: routeLocale = 'fr' } = useParams<{ locale: string }>()

  // Compatibilité ancien lien ?focus=subscription : redirige vers l'onglet
  // Facturation dédié au lieu de scroller en bas de la page Profil.
  useEffect(() => {
    if (!isManager) return
    if (searchParams.get('focus') === 'subscription') {
      navigate(`/${routeLocale}/manager?view=billing`, { replace: true })
    }
  }, [isManager, searchParams, navigate, routeLocale])

  // Acquitte le param ?focus si on est sur le dashboard agent
  useEffect(() => {
    if (isManager) return
    if (searchParams.get('focus')) {
      const next = new URLSearchParams(searchParams)
      next.delete('focus')
      setSearchParams(next, { replace: true })
    }
  }, [isManager, searchParams, setSearchParams])

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    )
  }

  const initials =
    `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() ||
    profile?.email?.[0]?.toUpperCase() ||
    '?'
  const isUploadingAvatar = uploadAvatar.isPending || deleteAvatar.isPending

  return (
    <div
      className={cn(
        'h-full flex flex-col overflow-hidden',
        darkMode ? 'bg-slate-900' : 'bg-slate-50',
      )}
    >
      {/* Header tabs (manager uniquement) — Profil / Facturation côte à côte.
          La page Facturation vit dans son propre onglet sidebar
          (?view=billing). Ce header est un raccourci visuel. */}
      {isManager && (
        <div
          className={cn(
            'flex-shrink-0 border-b',
            darkMode
              ? 'border-slate-800 bg-slate-900'
              : 'border-slate-200 bg-white',
          )}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-1" role="tablist" aria-label="Profile sections">
              <button
                type="button"
                role="tab"
                aria-selected="true"
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                  darkMode
                    ? 'border-white text-white'
                    : 'border-[#0A192F] text-[#0A192F]',
                )}
              >
                <UserIcon className="h-4 w-4" />
                {t('profile.tabs.profile', 'Profil')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected="false"
                onClick={() =>
                  navigate(`/${routeLocale}/manager?view=billing`)
                }
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                  'border-transparent',
                  darkMode
                    ? 'text-slate-400 hover:text-white hover:border-slate-700'
                    : 'text-slate-500 hover:text-slate-900 hover:border-slate-300',
                )}
              >
                <CreditCard className="h-4 w-4" />
                {t('profile.tabs.billing', 'Facturation')}
              </button>
            </nav>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header centré : avatar + nom + email + badge role */}
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleAvatarChange}
        />
        <div className="flex flex-col items-center text-center pt-4 pb-2">
          {/* Avatar cliquable */}
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            disabled={isUploadingAvatar}
            title={t('profile.uploadAvatar', 'Changer la photo')}
            className="relative h-24 w-24 group disabled:cursor-not-allowed"
          >
            <Avatar className="h-24 w-24 rounded-full overflow-hidden ring-2 ring-white dark:ring-slate-800 shadow-md">
              {profile?.avatar_url && (
                <AvatarImage
                  src={profile.avatar_url}
                  alt={initials}
                  className="h-full w-full object-cover aspect-square rounded-full"
                />
              )}
              <AvatarFallback
                className={cn(
                  'h-full w-full rounded-full text-2xl font-semibold flex items-center justify-center',
                  darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
                )}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            {/* Overlay caméra au hover */}
            {!isUploadingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-5 w-5 text-white" />
              </div>
            )}
            {/* Spinner pendant upload */}
            {isUploadingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              </div>
            )}
            {/* Badge caméra permanent quand pas de photo — indique que c'est cliquable */}
            {!profile?.avatar_url && !isUploadingAvatar && (
              <span
                className={cn(
                  'absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-full flex items-center justify-center shadow-md ring-2 transition-transform group-hover:scale-110',
                  darkMode
                    ? 'bg-indigo-500 ring-slate-900 text-white'
                    : 'bg-indigo-600 ring-white text-white',
                )}
              >
                <Camera className="h-3.5 w-3.5" />
              </span>
            )}
          </button>

          {/* Hint cliquable sous l'avatar quand pas de photo */}
          {!profile?.avatar_url && !isUploadingAvatar && (
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className={cn(
                'mt-3 text-xs font-medium transition-colors hover:underline',
                darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700',
              )}
            >
              {t('profile.addAvatar', 'Ajouter une photo de profil')}
            </button>
          )}

          {/* Nom + email */}
          <h1
            className={cn(
              'text-2xl font-bold tracking-tight mt-4',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {`${firstName} ${lastName}`.trim() || profile?.email || ''}
          </h1>
          {profile?.email && (
            <p
              className={cn(
                'text-sm mt-0.5',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {profile.email}
            </p>
          )}

          {/* Badge role */}
          {profile?.role && (
            <div className="mt-2 flex items-center gap-2">
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border',
                  darkMode
                    ? 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30'
                    : 'bg-indigo-50 text-indigo-700 border-indigo-200',
                )}
              >
                {profile.access ? t(`recordingAccess.roles.${profile.access.role}.label`) : profile.role}
              </span>
            </div>
          )}

          {/* Lien Supprimer la photo (uniquement si avatar présent) */}
          {profile?.avatar_url && !isUploadingAvatar && (
            <button
              type="button"
              onClick={() => void handleAvatarDelete()}
              className={cn(
                'mt-3 inline-flex items-center gap-1 text-xs transition-colors',
                darkMode
                  ? 'text-slate-500 hover:text-red-400'
                  : 'text-slate-400 hover:text-red-600',
              )}
            >
              <Trash2 className="h-3 w-3" />
              {t('profile.deleteAvatar', 'Supprimer la photo')}
            </button>
          )}
        </div>

        {/* Infos personnelles */}
        <Section
          title={t('profile.sections.personal', 'Informations personnelles')}
          darkMode={darkMode}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('profile.firstName', 'Prénom')}>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label={t('profile.lastName', 'Nom')}>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
            <Field label={t('profile.email', 'Email')}>
              <Input
                value={profile?.email || ''}
                disabled
                className="bg-slate-100 dark:bg-slate-800/50"
              />
            </Field>
            <Field
              label={
                <>
                  {t('profile.phone', 'Téléphone personnel')}
                  {phoneProven && (
                    <span className="ml-2 text-xs font-medium text-emerald-600">
                      {t('onboarding.welcome.verif.verified')}
                    </span>
                  )}
                </>
              }
            >
              <Input
                type="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 514..."
                disabled={!!linkedVia || phoneStep !== 'idle'}
                className={linkedVia ? 'bg-slate-100 dark:bg-slate-800/50' : undefined}
              />
              {showPhoneCode && (
                <PhoneVerification
                  phone={typedPhone}
                  locale={routeLocale}
                  etape={phoneStep}
                  onEtape={onPhoneStep}
                  sombre={false}
                />
              )}
            </Field>
            {linkedVia && (
              <p className="sm:col-span-2 -mt-2 text-xs text-slate-500 dark:text-slate-400">
                {t('profile.managedBy', 'Courriel et cellulaire gérés par {{name}}.', { name: linkedVia })}
              </p>
            )}
            <Field label={t('profile.jobTitle', 'Poste')}>
              <Input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder={t('profile.jobTitlePlaceholder', 'Ex: Agent commercial')}
              />
            </Field>
            <Field label={t('profile.language', 'Langue')}>
              <Select
                value={language}
                onValueChange={(v) => setLanguage(v as 'fr' | 'en')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        {/* Préférences téléphonie */}
        <Section
          title={t('profile.sections.phone', 'Préférences téléphonie')}
          darkMode={darkMode}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label={t('profile.preferredCallerId', 'Numéro sortant préféré')}
            >
              <Select
                value={preferredCallerId || '__none__'}
                onValueChange={(v) =>
                  setPreferredCallerId(v === '__none__' ? '' : v)
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('profile.noPreferredCallerId', 'Aucun')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">
                    {t('profile.noPreferredCallerId', 'Aucun')}
                  </SelectItem>
                  {availableCallerIds.map((n) => (
                    <SelectItem key={n.phone_number} value={n.phone_number}>
                      {n.phone_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label={t('profile.forwardNumber', 'Numéro de renvoi')}
            >
              <Input
                value={forwardNumber}
                onChange={(e) => setForwardNumber(e.target.value)}
                placeholder="+1 514..."
              />
            </Field>
          </div>
        </Section>

        {/* Notifications */}
        <Section
          title={t('profile.sections.notifications', 'Notifications')}
          darkMode={darkMode}
        >
          <ToggleRow
            label={t('profile.dnd', 'Ne pas déranger')}
            description={t(
              'profile.dndDesc',
              'Aucun appel ne sera transmis sur ce poste',
            )}
            checked={dndEnabled}
            onChange={setDndEnabled}
            darkMode={darkMode}
            icon={<BellOff className="h-4 w-4" />}
          />
          <ToggleRow
            label={t('profile.smsPush', 'Notifications SMS push')}
            description={t(
              'profile.smsPushDesc',
              'Recevoir une notification mobile pour chaque nouveau SMS',
            )}
            checked={smsPushEnabled}
            onChange={setSmsPushEnabled}
            darkMode={darkMode}
            icon={<UserIcon className="h-4 w-4" />}
          />
          <ToggleRow
            label={t('profile.smsEmail', 'Notification email pour chaque SMS')}
            description={t(
              'profile.smsEmailDesc',
              'Recevoir un email à chaque SMS reçu. Désactivé par défaut - le push et le dashboard suffisent généralement.',
            )}
            checked={smsEmailEnabled}
            onChange={setSmsEmailEnabled}
            darkMode={darkMode}
            icon={<Mail className="h-4 w-4" />}
          />
        </Section>

        <ScheduleSection darkMode={darkMode} />
        <CompanyNameSection darkMode={darkMode} />
        <CompanyTimezoneSection darkMode={darkMode} />
        <SecuritySection darkMode={darkMode} />
        <PasskeysSection darkMode={darkMode} />
        </div>
      </div>

      {/* Footer (flex-shrink-0) : visible uniquement si modifications non sauvegardées */}
      {isDirty && (
        <div
          className={cn(
            'flex-shrink-0 border-t shadow-lg',
            darkMode
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-slate-200',
          )}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
            <p
              className={cn(
                'text-sm',
                darkMode ? 'text-slate-300' : 'text-slate-700',
              )}
            >
              {t(
                'profile.unsavedChanges',
                'Modifications non enregistrées',
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetForm}
                disabled={updateProfile.isPending}
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={updateProfile.isPending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {updateProfile.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('profile.save', 'Sauvegarder')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// =====================================================
// Section Mon horaire personnel
// =====================================================

const DEFAULT_OPEN = '09:00'
const DEFAULT_CLOSE = '17:00'

function ScheduleSection({ darkMode }: { darkMode: boolean }) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'en' ? enUS : fr
  const { data: schedules = [], isLoading } = useAgentSchedule()
  const updateSchedule = useUpdateAgentSchedule()
  // Les horaires propres à chaque utilisateur sont inclus à partir de Team.
  const { allowed, planFor } = usePlanRights()
  const horairesPermis = allowed('horaires_utilisateur')
  const [draft, setDraft] = useState<AgentScheduleDay[]>([])
  const [editing, setEditing] = useState(false)

  // Noms des jours via date-fns avec la locale courante.
  // 2024-01-01 est un lundi → idx 0=lundi, 1=mardi, ..., 6=dimanche
  const dayNames = useMemo(
    () =>
      Array.from({ length: 7 }, (_, idx) => {
        const date = new Date(2024, 0, 1 + idx)
        const name = format(date, 'EEEE', { locale: dateLocale })
        return name.charAt(0).toUpperCase() + name.slice(1)
      }),
    [dateLocale],
  )

  const merged = useMemo(() => {
    return Array.from({ length: 7 }, (_, idx) => {
      const existing = schedules.find((s) => s.day === idx)
      return (
        existing || {
          day: idx,
          open_time: DEFAULT_OPEN,
          close_time: DEFAULT_CLOSE,
          is_active: false,
        }
      )
    })
  }, [schedules])

  useEffect(() => {
    if (!editing) setDraft(merged)
  }, [merged, editing])

  const handleStartEdit = () => {
    setDraft(merged)
    setEditing(true)
  }

  const handleCancel = () => {
    setDraft(merged)
    setEditing(false)
  }

  const handleSave = async () => {
    try {
      // Format HH:MM → HH:MM:SS (le backend Django attend des heures avec secondes)
      const payload = draft.map((d) => ({
        ...d,
        open_time: d.open_time
          ? d.open_time.length === 5
            ? `${d.open_time}:00`
            : d.open_time
          : null,
        close_time: d.close_time
          ? d.close_time.length === 5
            ? `${d.close_time}:00`
            : d.close_time
          : null,
      }))
      await updateSchedule.mutateAsync(payload)
      toast.success(t('profile.scheduleSaved', 'Horaire mis à jour'))
      setEditing(false)
    } catch {
      toast.error(t('profile.errors.scheduleSave', 'Erreur lors de la sauvegarde'))
    }
  }

  const rows = editing ? draft : merged

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <h2
          className={cn(
            'text-sm font-semibold flex items-center gap-2',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          <Calendar className="h-4 w-4 text-indigo-500" />
          {t('profile.sections.schedule', 'Mon horaire personnel')}
        </h2>
        {!editing && !isLoading && (
          <div className="flex items-center gap-2">
            {!horairesPermis && <PlanBadge plan={planFor('horaires_utilisateur')} />}
            <Button type="button" variant="outline" size="sm" onClick={handleStartEdit} disabled={!horairesPermis}>
              {t('profile.editSchedule', 'Modifier')}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => {
            const update = (patch: Partial<AgentScheduleDay>) => {
              setDraft((prev) => {
                const next = [...prev]
                next[idx] = { ...next[idx], ...patch }
                return next
              })
            }
            return (
              <div
                key={row.day}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 border',
                  darkMode
                    ? 'border-slate-800 bg-slate-900/40'
                    : 'border-slate-100 bg-slate-50/60',
                )}
              >
                <span
                  className={cn(
                    'text-xs font-medium w-20 flex-shrink-0',
                    darkMode ? 'text-slate-300' : 'text-slate-700',
                  )}
                >
                  {dayNames[idx]}
                </span>
                <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={row.is_active}
                    disabled={!editing}
                    onChange={(e) => update({ is_active: e.target.checked })}
                  />
                  <div
                    className={cn(
                      "w-8 h-4 rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-full relative",
                      darkMode ? 'bg-slate-700' : 'bg-slate-300',
                      !editing && 'opacity-60',
                    )}
                  />
                </label>
                {row.is_active ? (
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      type="time"
                      value={(row.open_time || DEFAULT_OPEN).slice(0, 5)}
                      onChange={(e) => update({ open_time: e.target.value })}
                      disabled={!editing}
                      className="w-28 h-7 text-xs"
                    />
                    <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
                      →
                    </span>
                    <Input
                      type="time"
                      value={(row.close_time || DEFAULT_CLOSE).slice(0, 5)}
                      onChange={(e) => update({ close_time: e.target.value })}
                      disabled={!editing}
                      className="w-28 h-7 text-xs"
                    />
                  </div>
                ) : (
                  <span
                    className={cn(
                      'flex-1 text-xs italic',
                      darkMode ? 'text-slate-600' : 'text-slate-400',
                    )}
                  >
                    {t('profile.dayClosed', 'Fermé')}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {editing && (
        <div className="flex justify-end gap-2 mt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={updateSchedule.isPending}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={updateSchedule.isPending}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {updateSchedule.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('profile.save', 'Sauvegarder')}
          </Button>
        </div>
      )}
    </section>
  )
}

// =====================================================
// Section Sécurité — changement de mot de passe
// =====================================================

function SecuritySection({ darkMode }: { darkMode: boolean }) {
  const { t } = useTranslation()
  const changePassword = useChangePassword()
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState('')
  const [next1, setNext1] = useState('')
  const [next2, setNext2] = useState('')

  const reset = () => {
    setCurrent('')
    setNext1('')
    setNext2('')
    setOpen(false)
  }

  const handleSubmit = async () => {
    if (!current || !next1 || !next2) {
      toast.error(
        t('profile.errors.passwordRequired', 'Tous les champs sont requis'),
      )
      return
    }
    if (next1.length < 8) {
      toast.error(
        t('profile.errors.passwordShort', 'Au moins 8 caractères requis'),
      )
      return
    }
    if (next1 !== next2) {
      toast.error(
        t('profile.errors.passwordMismatch', 'Les mots de passe ne correspondent pas'),
      )
      return
    }
    try {
      await changePassword.mutateAsync({
        old_password: current,
        new_password1: next1,
        new_password2: next2,
      })
      toast.success(
        t('profile.passwordChanged', 'Mot de passe mis à jour'),
      )
      reset()
    } catch {
      toast.error(
        t('profile.errors.passwordChange', 'Mot de passe actuel incorrect'),
      )
    }
  }

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <h2
          className={cn(
            'text-sm font-semibold flex items-center gap-2',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          <KeyRound className="h-4 w-4 text-indigo-500" />
          {t('profile.sections.security', 'Sécurité')}
        </h2>
        {!open && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
          >
            {t('profile.changePassword', 'Changer mon mot de passe')}
          </Button>
        )}
      </div>
      <p
        className={cn(
          'text-xs',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t(
          'profile.securityHint',
          "Mettez à jour votre mot de passe régulièrement pour sécuriser votre compte.",
        )}
      </p>

      {open && (
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs font-medium">
              {t('profile.currentPassword', 'Mot de passe actuel')}
            </Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs font-medium">
              {t('profile.newPassword', 'Nouveau mot de passe')}
            </Label>
            <Input
              type="password"
              value={next1}
              onChange={(e) => setNext1(e.target.value)}
              placeholder={t(
                'profile.newPasswordPlaceholder',
                'Au moins 8 caractères',
              )}
            />
          </div>
          <div>
            <Label className="text-xs font-medium">
              {t('profile.confirmPassword', 'Confirmer le nouveau mot de passe')}
            </Label>
            <Input
              type="password"
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit()
              }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={reset}
              disabled={changePassword.isPending}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleSubmit()}
              disabled={changePassword.isPending}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {changePassword.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('profile.changePassword', 'Changer mon mot de passe')}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

interface SectionProps {
  title: string
  darkMode: boolean
  children: React.ReactNode
}

function Section({ title, darkMode, children }: SectionProps) {
  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <h2
        className={cn(
          'text-sm font-semibold mb-4',
          darkMode ? 'text-white' : 'text-slate-900',
        )}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

interface FieldProps {
  label: React.ReactNode
  children: React.ReactNode
}

function Field({ label, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
    </div>
  )
}

interface ToggleRowProps {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
  darkMode: boolean
  icon?: React.ReactNode
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  darkMode,
  icon,
}: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-2 first:pt-0">
      <div className="flex items-start gap-3">
        {icon && (
          <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400', 'mt-0.5')}>
            {icon}
          </span>
        )}
        <div>
          <p
            className={cn(
              'text-sm font-medium',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {label}
          </p>
          <p
            className={cn(
              'text-xs mt-0.5',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {description}
          </p>
        </div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div
          className={cn(
            "w-10 h-6 rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full",
            darkMode ? 'bg-slate-700' : 'bg-slate-300',
          )}
        />
      </label>
    </div>
  )
}
